<template>
  <VDataGridV2
    id="demands-v2-registry"
    :loader="dataLoader"
    :context-menu="DemandsV2Service.contextMenu"
    paginated
    striped
    entity-type="demandV2"
    layout="fill"
    @row:dblclick="openDemandCard"
  >
    <VDataGridColumnV2
      v-if="isColumnVisible('id')"
      id="id"
      label="№"
      width="80px"
      sort-default-value="desc"
    >
      <template #default="{ row }">
        <VLink @click="openDemandCard(row)">{{ row.id }}</VLink>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('seller')"
      id="seller"
      width="150px"
      content-class="ellipsis"
    >
      <template #label>
        Поставщик
        <div class="description">Юр. лицо</div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis" :title="getSellerFullInfoByField(row, 'seller')">
          {{ getSellerShortInfoByField(row, 'seller') }}
        </div>
        <div class="description ellipsis" :title="getSellerFullInfoByField(row, 'sellerCompany')">
          {{ getSellerShortInfoByField(row, 'sellerCompany') }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('name')"
      id="name"
      width="250px"
      content-class="ellipsis"
      filterable
      filter-send-name="pattern"
      :filter-props="{ placeholder: 'Поиск по названию', clearable: true }"
    >
      <template #label>
        Наименование
        <div
          v-show="isColumnVisible('manufacturer')"
          class="description"
        >
          Производитель
        </div>
      </template>
      <template #default="{ row }">
        <div class="ellipsis" :title="row.name">
          {{ row.name }}
        </div>
        <div
          v-show="isColumnVisible('manufacturer')"
          class="description ellipsis"
          :title="getSellerFullInfoByField(row, 'manufacturer')"
        >
          {{ getSellerShortInfoByField(row, 'manufacturer') }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('qty')"
      id="qty"
      content-align="right"
      width="100px"
    >
      <template #label>
        <div class="nowrap">Общее кол-во</div>
      </template>
      <template #default="{ row }">
        <div class="nowrap">
          {{ row.qtyDesired?.format(row.unit) }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('qtyOrdered')"
      id="qtyOrdered"
      content-align="right"
      width="100px"
    >
      <template #label>
        <div class="nowrap">Заказано</div>
      </template>
      <template #default="{ row }">
        <div class="nowrap">{{ row.qtyOrdered?.format() || '--' }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('qtyLeft')"
      id="qtyLeft"
      content-align="right"
      width="100px"
    >
      <template #label>
        <div class="nowrap">Осталось</div>
      </template>
      <template #default="{ row }">
        <div class="nowrap">{{ row.qtyLeft?.format() || '--' }}</div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('price')"
      id="price"
      width="140px"
      content-align="right"
      hint="Общая сумма заказа / Наибольшая цена из выбранных вариантов"
    >
      <template #label>
        Общая сумма
        <div class="description">Макс цена</div>
      </template>

      <template #default="{ row }">
        <!-- Общая сумма по выбранным вариантам -->
        <div class="nowrap">
          {{ row.sumFullSeller?.format(row.currencyCode) || '--' }}
        </div>

        <!-- Макс цена: цвет и тултип считает модель, клик открывает историю -->
        <div class="description nowrap">
          <VIcon
            v-if="row.chosenOptions.length === 1"
            material="warning"
            style="color: var(--warning-strong)"
            theme="outlined"
          />
          <VLink
            :style="{ color: row.getPriceDifferenceColor() }"
            :title="row.getPriceDifferenceTooltip()"
            @click="openPrevDealsPopup($event, row)"
          >
            {{ row.maxPrice?.format(row.currencyCode, row.unit) || '--' }}
          </VLink>
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('minDesiredReceiptDate')"
      id="minDesiredReceiptDate"
      width="155px"
      label="Поставка"
      content-align="center"
      filterable
      filter-type="date-range"
      filter-send-name="desiredReceiptPeriod"
      :filter-props="{ placeholder: 'Интервал получения', clearable: true }"
    >
      <template #label>
        Поставка
        <div class="description">
          Ближайшая дата
        </div>
      </template>
      <template #default="{ row }">
        <div
          class="nowrap"
          :class="getDeliveryDateClass(row.minDesiredReceiptDate)"
          @mouseenter="$core.tooltip.show($event.currentTarget, getDeliveryDateTooltip(row.minDesiredReceiptDate))"
          @mouseleave="$core.tooltip.hide()"
        >
          {{ $core.utils.date.humanizeDate(row.minDesiredReceiptDate, null) }}
          <VIcon
            v-if="getDeliveryDateTooltip(row.minDesiredReceiptDate)"
            theme="outlined"
            material="help_outline"
          />
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('buyerProductionSite')"
      id="buyerProductionSite"
      label="Площадка"
      width="100px"
      content-align="center"
      content-class="ellipsis"
      filterable
      filter-type="select"
      filter-send-name="buyerProductionSite"
      :filter-props="productionSiteFilterProps"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.buyerProductionSite?.name"
        >
          {{ row.buyerProductionSite?.name || '--' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('status')"
      id="status"
      label="Статус"
      content-align="center"
      width="150px"
      content-class="nowrap"
    >
      <template #default="{ row }">
        {{ DemandsV2Service.getStageTab(row)?.name ?? '--' }}
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('isApproved')"
      id="isApproved"
      label="Утверждено"
      content-align="center"
      width="100px"
    >
      <template #default="{ row }">
        <VSwitch
          theme="slider"
          checked-label="Да"
          unchecked-label="Нет"
          v-model="row.isApproved"
          :disabled="isDisabledDemand(row.id)"
          @update:modelValue="onIsApprovedInput(row, $event)"
        />
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('comment')"
      id="comment"
      width="280px"
      label="Комментарий"
      content-class="ellipsis"
    >
      <template #default="{ row }">
        <LastComment
          :entity="row"
          entity-type-name="demandV2"
        />
      </template>
    </VDataGridColumnV2>

    <VDataGridColumnV2
      v-if="isColumnVisible('responsible')"
      id="responsible"
      label="Ответственный"
      content-class="ellipsis"
      width="140px"
      filterable
      filter-type="select"
      filter-send-name="responsible"
      :filter-props="{
        clearable:      true,
        placeholder:    'Введите ФИО',
        optionIdentity: 'personId',
        searchFunction: searchResponsible,
      }"
    >
      <template #default="{ row }">
        <div
          class="ellipsis"
          :title="row.responsibleBuyer?.name"
        >
          {{ row.responsibleBuyer?.name || '--' }}
        </div>
      </template>
    </VDataGridColumnV2>

    <VDataGridActionBarV2>
      <template #default>
        <div class="manual-order-actions">
          <VButton @click="openDemandCard(null)">
            Создать потребность
          </VButton>
        </div>
      </template>
    </VDataGridActionBarV2>
  </VDataGridV2>

  <DemandV2PrevDealsPopup
    :visible="Boolean(prevDealsPopup.demandV2)"
    :target="prevDealsPopup.target"
    :demand-v2="prevDealsPopup.demandV2"
    @renew-data="renewPrevDealsPopup"
    @close="closePrevDealsPopup"
  />
</template>

<style src="./DemandV2Registry.scss" />

<script lang="ts">

import DemandsV2Service       from '@/domains/DemandsV2/DemandsV2Service';
import DemandV2               from '@/domains/DemandsV2/DemandV2';
import PersonsService         from '@/domains/Persons/PersonsService';
import ProductionSitesService from '@/domains/ProductionSites/ProductionSitesService';
import DateSimple             from '@/core/types/DateSimple';
import DataGridLoader         from '@/lib/components/VDataGridV2/DataGridLoader';

import type Person            from '@/domains/Persons/Person';

import DemandV2PrevDealsPopup from "@/domains/DemandsV2/components/DemandV2Registry/DemandV2PrevDealsPopup.vue";

export default defineComponent({
  name: 'DemandV2Registry',

  components: {
    DemandV2PrevDealsPopup,
  },

  props: {
    /**
     * Внешние фильтры реестра (флаги жизненного цикла + прочее).
     * Формируются на странице по активной вкладке, уходят в загрузчик.
     */
    params: {
      type:    Object as PropType<Parameters<typeof DemandsV2Service.getListPaginated>[0]>,
      default: null,
    },

    /**
     * Скрытые колонки (чёрный список). Формируются на странице по активной вкладке.
     */
    hiddenColumns: {
      type:    Array as PropType<string[]>,
      default: () => [],
    },

    /**
     * Активна ли индикация даты поставки (срочность/просрочка).
     * Считается на странице — реестр про вкладки не знает.
     */
    showDeliveryDateIndication: {
      type:    Boolean,
      default: false,
    },
  },

  setup() {
    return {
      DemandsV2Service,
    };
  },

  data() {
    return {
      // Загрузчик создаём в created() — в data() computeds ещё не на instance
      // и watch внутри useReactiveSource не отследит зависимости
      dataLoader: null as unknown as DataGridLoader<any, DemandV2>,

      /**
       * Если пользователь ошибся в реквизите согласованности, нужно успеть
       * актуализировать версию записи перед тем как он "отожмёт" согласованность.
       * Чтобы не играть в перегонки - дизейблим контрол на время апдейта
       */
      disabledDemandId: null as number | null,

      prevDealsPopup: {
        target:   null as HTMLElement | null,
        demandV2: null as DemandV2 | null,
      },

      /**
       * Источник опций фильтра площадок: опции и их загрузку держит сам лоадер.
       * Объект источника не заменять — VSelectV2 берёт источник из пропа data
       * один раз при создании и смену пропа не отслеживает.
       * siteId — примитивный идентификатор опции (id у неё — объект для бэкенда),
       * см. ProductionSitesService.getFilterOptions.
       */
      productionSiteFilterProps: {
        clearable:   true,
        placeholder: 'Все',
        data:        this.$core.loader.select(() => ProductionSitesService.getFilterOptions(this.$core.currentPartner))
          .setIdentity('siteId')
          .preloadOnMount(),
      },
    };
  },

  created() {
    this.dataLoader = new DataGridLoader<any, DemandV2>(DemandsV2Service.getListPaginated.bind(DemandsV2Service))
      .useReactiveSource(() => ({
        ...this.params,
      }));
  },

  computed: {
    hiddenColumnsSet(): Set<string> {
      return new Set(this.hiddenColumns);
    },
  },

  methods: {
    /** Видна ли колонка — чёрный список приходит сверху */
    isColumnVisible(columnId: string): boolean {
      return this.hiddenColumnsSet.has(columnId) === false;
    },

    /**
     * Поиск ответственного для фильтра. Опция несёт id как объект { id } —
     * именно его бэкенд ждёт в параметре responsible (responsible->id)
     */
    async searchResponsible(pattern: string): Promise<any[]> {
      const persons = await PersonsService.searchPersons(pattern, this.$core.currentPartner?.id);
      return persons.map(person => this.toResponsibleOption(person));
    },

    /**
     * Опция ответственного для фильтра. id вложен объектом т.к.
     * бэкенд ждёт его в параметре responsible (responsible->id)
     */
    toResponsibleOption(person: Person): any {
      return {
        personId: person.id,
        name:     person.name,
        id:       { id: person.id },
      };
    },

    /** Производственные площадки текущего партнёра в виде опций фильтра. */
    async loadProductionSiteOptions(): Promise<any[]> {
      if (!this.$core.currentPartner?.id) return [];

      const sites = await ProductionSitesService.getList(new Partner(this.$core.currentPartner.id));

      return sites.map(site => this.toProductionSiteOption(site));
    },

    /**
     * Опция площадки для фильтра. id вложен объектом т.к.
     * бэкенд ждёт его в параметре buyerProductionSite (buyerProductionSite->id)
     */
    toProductionSiteOption(site: ProductionSite): any {
      return {
        siteId: site.id,
        name:   site.name,
        id:     { id: site.id },
      };
    },

    /**
     * Возвращает короткое представление по полю опции - первое значение,
     * а если их больше - "первое и ещё N..."
     */
    getSellerShortInfoByField(row: DemandV2, field: string): string | null {
      const options = this.getSetUniqueValuesByField(row.options, field);
      if (options.size === 0) return null;

      const firstItem = options.values().next().value;
      if (options.size < 2) return firstItem as string;

      return `${firstItem} и ещё ${options.size - 1}...`;
    },

    /**
     * Возвращает полный список значений по полю опции через запятую (для tooltip)
     */
    getSellerFullInfoByField(row: DemandV2, field: string): string {
      return Array.from(this.getSetUniqueValuesByField(row.options, field)).join(', ');
    },

    getSetUniqueValuesByField(options: any[], field: string): Set<string> {
      // el[field] бывает пустым (например manufacturer не гидрируется в опции) - пропускаем
      return options.reduce((acc, el) => {
        const name = el[field]?.name;
        if (name) acc.add(name);
        return acc;
      }, new Set<string>());
    },

    /**
     * Переключение switch'а согласованности. version-sync делает сервис (setApproved),
     * на время апдейта дизейблим контрол чтобы не словить version mismatch при быстрых кликах
     */
    async onIsApprovedInput(row: DemandV2, event: boolean): Promise<void> {
      this.disableIsApprovedBtn(row.id);
      await DemandsV2Service.setApproved(row, event);
      this.allowIsApprovedBtn();
    },

    /**
     * Открывает попап истории сделок и вариантов.
     * В реестре getList не отдаёт prevDeals — тянем полную потребность по id.
     */
    async openPrevDealsPopup(event: MouseEvent, row: DemandV2): Promise<void> {
      this.$core.tooltip.hide();

      this.prevDealsPopup.target   = event.currentTarget as HTMLElement;
      this.prevDealsPopup.demandV2 = await DemandsV2Service.get(row.id);
    },

    /**
     * В попапе изменили варианты — перечитываем потребность для попапа
     * и обновляем реестр.
     */
    async renewPrevDealsPopup(): Promise<void> {
      this.prevDealsPopup.demandV2 = await DemandsV2Service.get(this.prevDealsPopup.demandV2!.id);
      this.dataLoader.refresh();
    },

    closePrevDealsPopup(): void {
      this.prevDealsPopup.target   = null;
      this.prevDealsPopup.demandV2 = null;
    },

    isDisabledDemand(demandId: number): boolean {
      return this.disabledDemandId === demandId;
    },

    disableIsApprovedBtn(demandId: number): void {
      this.disabledDemandId = demandId;
    },

    allowIsApprovedBtn(): void {
      this.disabledDemandId = null;
    },

    /**
     * Разница в днях между сегодня и датой поставки. null если даты нет.
     * Отрицательное значение = просрочка.
     * Дата уже гидрирована в DateSimple в DemandV2.fromServer
     */
    getDeliveryDaysLeft(date: DateSimple | null): number | null {
      if (!date) return null;

      return date.diffInDays(DateSimple.today());
    },

    /**
     * CSS-класс для ячейки даты поставки. Красный - просрочена,
     * оранжевый - осталось 3 и менее дней. Индикация приходит пропом сверху
     */
    getDeliveryDateClass(date: DateSimple | null): string | null {
      if (!this.showDeliveryDateIndication) return null;

      const daysLeft = this.getDeliveryDaysLeft(date);
      if (daysLeft === null) return null;

      if (daysLeft < 0) return 'delivery-date-overdue';
      if (daysLeft <= DemandV2.DELIVERY_DATE_WARNING_DAYS) return 'delivery-date-warning';
      return null;
    },

    /**
     * Текст тултипа для цветной даты поставки
     */
    getDeliveryDateTooltip(date: DateSimple | null): string | null {
      if (!this.showDeliveryDateIndication) return null;

      const daysLeft = this.getDeliveryDaysLeft(date);
      if (daysLeft === null) return null;

      if (daysLeft < 0) return 'Дата поставки просрочена';
      if (daysLeft <= DemandV2.DELIVERY_DATE_WARNING_DAYS) {
        return `До даты поставки осталось ${daysLeft} ${this.$core.utils.string.pluralizeRu(daysLeft, 'день', 'дня', 'дней')}`;
      }
      return null;
    },

    /**
     * Открывает карточку потребности. null — создание новой.
     * Реестр перезагружаем только если карточка вернула true (были изменения).
     */
    async openDemandCard(demand: DemandV2 | null): Promise<void> {
      if (await DemandsV2Service.showDemandV2Card(demand) === true) {
        await this.dataLoader.refresh();
      }
    },
  },
});
</script>
